import React from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import type { BottomTabBarProps } from '@react-navigation/bottom-tabs';

import { getTabIconConfig, getTabLabel } from '../../../core/navigation/tabIconConfig';
import { colors, spacing, typography } from '../../../core/theme/tokens';
import { TabIcon } from '../icons/TabIcon';
import { GlassSurface } from '../glass';

type GlassTabBarItemsProps = BottomTabBarProps & {
  isWeb?: boolean;
};

export function GlassTabBarItems({
  state,
  descriptors,
  navigation,
  isWeb = false,
}: GlassTabBarItemsProps) {
  return (
    <View style={styles.row}>
      {state.routes.map((route, index) => {
        const { options } = descriptors[route.key];
        const isFocused = state.index === index;
        const label = getTabLabel(route.name) ?? options.title ?? route.name;
        const iconConfig = getTabIconConfig(route.name);
        const tint = isFocused ? colors.accent : colors.tabBarInactive;

        const onPress = () => {
          const event = navigation.emit({
            type: 'tabPress',
            target: route.key,
            canPreventDefault: true,
          });
          if (!isFocused && !event.defaultPrevented) {
            navigation.navigate(route.name);
          }
        };

        const tabContent = (
          <>
            {iconConfig ? (
              <TabIcon name={iconConfig.web} color={tint} focused={isFocused} />
            ) : null}
            <Text style={[styles.label, isFocused && styles.labelFocused]} numberOfLines={1}>
              {label}
            </Text>
          </>
        );

        return (
          <Pressable
            key={route.key}
            accessibilityRole="button"
            accessibilityState={isFocused ? { selected: true } : {}}
            accessibilityLabel={options.tabBarAccessibilityLabel ?? label}
            onPress={onPress}
            style={[styles.tab, isWeb && styles.tabWeb]}
          >
            {isFocused ? (
              <GlassSurface tint="neutral" rounded="pill" style={styles.activePill}>
                <View style={styles.tabInner}>{tabContent}</View>
              </GlassSurface>
            ) : (
              <View style={styles.tabInner}>{tabContent}</View>
            )}
          </Pressable>
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'stretch',
    paddingHorizontal: spacing.xs,
    paddingVertical: spacing.xs,
  },
  tab: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 49,
  },
  tabWeb: {
    cursor: 'pointer',
  },
  activePill: {
    width: '100%',
    maxWidth: 88,
  },
  tabInner: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 6,
    paddingBottom: 4,
    gap: 3,
    minHeight: 44,
  },
  label: {
    ...typography.caption,
    fontSize: 10,
    fontWeight: '500',
    color: colors.tabBarInactive,
    letterSpacing: 0.2,
  },
  labelFocused: {
    color: colors.accent,
    fontWeight: '600',
  },
});
